import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { refreshFeatures } from "@/lib/features";
import { CheckCircle, XCircle, Power } from "@phosphor-icons/react";

export default function TeachersPanel() {
  const [reg, setReg] = useState(null);
  const [nda, setNda] = useState(null);
  const [feat, setFeat] = useState(null);
  const [ndaText, setNdaText] = useState("");
  const [reqAdmins, setReqAdmins] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/admin/teacher-registry").then(({ data }) => setReg(data)).catch(() => setReg({ teachers: [] }));
    api.get("/admin/nda-config").then(({ data }) => { setNda(data); setNdaText(data.text); setReqAdmins(data.require_school_admins); });
    api.get("/features").then(({ data }) => setFeat(data));
  };
  useEffect(load, []);

  const toggleFeatures = async () => {
    const next = !feat?.instructor_features;
    try {
      await api.put("/admin/features", { instructor_features: next });
      await refreshFeatures();
      toast.success(next ? "Instructor features turned ON" : "Instructor features turned OFF");
      window.location.reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveNda = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/nda-config", { text: ndaText, require_school_admins: reqAdmins });
      setNda(data);
      toast.success("NDA saved");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" }) : "");

  const downloadNdaPdf = async (t) => {
    try {
      const { data } = await api.get(`/admin/nda-signatures/${t.user_id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = `Teaching-NDA-${(t.nda_typed_name || t.name || "teacher").replace(/ /g, "-")}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="teachers-panel">
      <div className="card">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-lg font-bold font-display flex items-center gap-2"><Power size={18} /> Instructor features (legacy)</h2>
            <p className="text-xs text-[#000000] mt-1 max-w-xl">
              The train-the-trainer system: Level 1/2 instructor roles, certification, profit sharing, instructor payouts,
              referral codes and instructor promo codes. Nothing is deleted when off — everything is hidden and disabled,
              and can be turned back on here anytime.
            </p>
          </div>
          <button className={feat?.instructor_features ? "btn-primary text-sm" : "btn-secondary text-sm"}
            onClick={toggleFeatures} data-testid="instructor-features-toggle">
            {feat === null ? "…" : feat.instructor_features ? "ON — click to turn OFF" : "OFF — click to turn ON"}
          </button>
        </div>
      </div>

      <div className="card">
        <h2 className="text-lg font-bold font-display mb-1">Teacher registry — permanent</h2>
        <p className="text-xs text-[#000000] mb-3">Every person who has ever held the Teacher role stays in this library, even if their account is later removed or their role changes.</p>
        {reg === null ? <div className="text-sm text-[#000000]">Loading…</div> : reg.teachers.length === 0 ? (
          <div className="text-sm text-[#000000]" data-testid="teacher-registry-empty">No teachers yet — anyone given the Teacher role appears here automatically.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="teacher-registry-table">
              <thead><tr className="text-left text-[#000000] uppercase text-xs">
                <th className="p-2">Name</th><th className="p-2">Email</th><th className="p-2">School</th>
                <th className="p-2">Status</th><th className="p-2">First added</th><th className="p-2">NDA</th>
              </tr></thead>
              <tbody>
                {reg.teachers.map((t) => (
                  <tr key={t.user_id} className="border-t border-[#E4E4E7]" data-testid={`teacher-row-${t.user_id}`}>
                    <td className="p-2 font-semibold">{t.name || "—"}</td>
                    <td className="p-2">{t.email || "—"}</td>
                    <td className="p-2">{t.school_name || "—"}</td>
                    <td className="p-2">{t.is_deleted ? <span className="text-[#B45309]">Removed</span> : <span className="text-[#3F6212]">Active</span>}</td>
                    <td className="p-2 text-xs">{fmtDate(t.first_seen)}</td>
                    <td className="p-2">
                      {t.nda_signed
                        ? <span className="inline-flex items-center gap-2 text-[#3F6212] text-xs font-semibold flex-wrap">
                            <span className="inline-flex items-center gap-1"><CheckCircle size={14} weight="fill" /> Signed {fmtDate(t.nda_signed_at)}{t.nda_typed_name ? ` — "${t.nda_typed_name}"` : ""}</span>
                            <button className="btn-ghost text-xs underline text-[#1D4ED8] p-0 cursor-pointer" onClick={() => downloadNdaPdf(t)} data-testid={`nda-pdf-${t.user_id}`}>PDF</button>
                          </span>
                        : <span className="inline-flex items-center gap-1 text-[#B91C1C] text-xs font-semibold"><XCircle size={14} /> Not signed</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {reg?.admin_signatures?.length > 0 && (
          <div className="mt-3 text-xs text-[#000000]">
            <b>Admin signatures:</b> {reg.admin_signatures.map((s) => `${s.name || s.email} (${fmtDate(s.signed_at)})`).join(", ")}
          </div>
        )}
      </div>

      <div className="card">
        <h2 className="text-lg font-bold font-display mb-1">Teaching NDA — confidentiality & non-compete</h2>
        <p className="text-xs text-[#000000] mb-2">
          Every teacher must read and sign this before they can use the app — a full-screen signing step appears on their
          next login until they sign. Signatures (typed legal name, date, IP, and the exact text they signed) are stored
          permanently and shown in the registry above. Unsigned teachers get an automatic email/text reminder every 7 days
          (starting one week after their account is created), and both you and the teacher can download a signed PDF copy.
        </p>
        <div className="text-xs text-[#B45309] font-semibold mb-3">Legal template — have an Alberta lawyer review it before relying on it in a dispute.</div>
        <label className="flex items-center gap-2 text-xs font-semibold text-[#000000] mb-3 cursor-pointer">
          <input type="checkbox" checked={reqAdmins} onChange={(e) => setReqAdmins(e.target.checked)} data-testid="nda-require-admins" />
          Also require School Admins & Board Admins to sign
        </label>
        <textarea className="input-base w-full font-mono text-xs leading-relaxed" rows={22}
          value={ndaText} onChange={(e) => setNdaText(e.target.value)} data-testid="nda-editor" />
        <div className="flex items-center gap-3 mt-2">
          <button className="btn-primary text-sm" onClick={saveNda} disabled={busy} data-testid="save-nda-button">{busy ? "Saving…" : "Save NDA"}</button>
          {nda && <span className="text-xs text-[#000000]">Current version: {nda.version}</span>}
        </div>
      </div>
    </div>
  );
}
